import * as apiPostProduct from 'api/erp/PostProduct';
import * as apiGetProductMatch from 'api/match/GetMatchProductNew';
import { ErpSources } from 'enums/ErpSources';
import { Functor } from 'functions/Functor';
import * as OrderGuide from 'types/Recipe/Detail/ErpNew/OrderGuide';
import * as Product from 'types/Recipe/Detail/ErpNew/Product';
import { Record } from 'types/_general/Record';

export * as ProductList from 'types/Recipe/Detail/ErpNew/ProductList';

export type Type = Product.Type[];

export function create(): Type {
	return [];
}

export async function getFromApiSggestions(
	ingredientIds: string[],
	additionalParams?: {
		type?: 'id' | 'name';
		amountOfSuggestions?: number;
	}
): Promise<apiGetProductMatch.ResponseData> {
	if (!ingredientIds.length) return {};
	const amountOfSuggestions = additionalParams?.amountOfSuggestions ?? 1;
	const response = await apiGetProductMatch.callApi({
		ingredient: ingredientIds,
		type: additionalParams?.type ?? 'id',
		source: [],
		cultureCode: '',
		isCatStrict: false,
		pageSize: amountOfSuggestions,
		pageIndex: 0,
	});
	return response.getOrDefault({});
}

export function mapFromApiSuggestions(response: apiGetProductMatch.ResponseData): Type {
	const output: Type = [];
	for (const [sourceId, ingredientObject] of Object.entries(response)) {
		for (const [ingredientId, value] of Object.entries(ingredientObject)) {
			for (const product of value) {
				output.push({
					productId: product.productId,
					ingredientId: ingredientId,
					sourceId: sourceId,
					name: product.name,
					imageUrl: product.imageUrl,
					price: product.price ?? 0,
					gtin: product.externalId,
					nutrients: product.nutrients,
					size: Product.getSize(product.volume, product.weight),
					externalUrl: product.externalUrl,
				});
			}
		}
	}
	return output;
}

export function mapToApi(
	input: { productId: string; sourceId: string }[]
): apiPostProduct.Request[] {
	const output: apiPostProduct.Request[] = [];
	for (const i of input) {
		let found = false;
		for (const z of output) {
			if (z.erpName !== i.sourceId) continue;
			if (!z.productIds.includes(i.productId)) {
				z.productIds.push(i.productId);
			}
			found = true;
		}
		if (found) continue;
		output.push({ erpName: i.sourceId, productIds: [i.productId] });
	}
	return output;
}

export async function getFromApi(input: apiPostProduct.Request[]): Promise<Type> {
	let output: Type = [];
	for (const i of input) {
		const response = await Functor.From(i)
			.mapAsync(apiPostProduct.callApi)
			.map((e) => e.getOrDefault({}))
			.map((e) => mapFromApi(e, i.erpName))
			.get();
		output = [...output, ...response];
	}
	return output;
}

export function mapFromApi(input: apiPostProduct.ResponseData, sourceId: string): Type {
	const output: Type = [];
	for (const [productId, product] of Object.entries(input)) {
		output.push({
			...product,
			productId: productId,
			gtin: product.externalId ?? '',
			ingredientId: '',
			sourceId: sourceId,
			name: product.name ?? '',
			imageUrl: product.imageUrl ?? '',
			externalUrl: product.externalUrl ?? '',
			price: product.price,
			nutrients: product.nutrients ? Record.removeNullValues(product.nutrients) : {},
			size: Product.getSize(product.volume, product.weight),
		});
	}
	return output;
}

export function sortByPriority(obj: Type): Type {
	return obj.sort((a, b) => {
		const priorityA: number = a.priority ?? 0;
		const priorityB: number = b.priority ?? 0;
		if (priorityA === priorityB) return 0;
		if (priorityB === 0) return -1;
		if (priorityA === 0) return 1;
		if (priorityA > priorityB) return 1;
		if (priorityA < priorityB) return -1;
		return 0;
	});
}

export function getById(obj: Type, id: string): Product.Type | undefined {
	return obj.find((e) => e.productId === id);
}

export function getSelected(
	obj: Type,
	manuallySearchedProducts: Type | null
): Product.Type | undefined {
	if (manuallySearchedProducts) {
		return manuallySearchedProducts.find((e) => e.selected === true);
	}
	return obj.find((e) => e.selected === true);
}

export function selectFirstProduct(obj: Type): Type {
	if (!obj[0]) return obj;
	return [Product.select(obj[0]), ...obj.slice(1)];
}

export function applyOrderGuidePriority(obj: Type, orderGuide: OrderGuide.Type): Type {
	return obj.map((e) => Product.applyOrderGuidePriority(e, orderGuide));
}

export function mergeTwoLists(doNotChange: Type, doAdd: Type): Type {
	const output: Type = [...doNotChange];
	for (const p of doAdd) {
		const product = doNotChange.find((e) => e.productId === p.productId);
		if (product) continue;
		output.push({ ...p });
	}
	return output;
}

export function applyIngredientIdToProducts(obj: Type, ingredientId: string): Type {
	return obj.map((e) => ({
		...e,
		ingredientId: ingredientId,
	}));
}

export function getByIngredientId(products: Type, ingredinetId: string): Product.Type | undefined {
	const product = products.find(
		(e) => e.ingredientId === ingredinetId && e.sourceId === ErpSources.DEFAULT
	);
	if (!product) return undefined;
	return {
		...product,
		sourceId: 'ingredient',
	};
}

export function addProduct(obj: Type, product: Product.Type): Type {
	if (obj.find((e) => e.productId === product.productId)) return obj;
	return [...obj, product];
}

export function removeProduct(obj: Type, product: Product.Type): Type {
	return obj.filter((e) => e.productId !== product.productId);
}

export function deselectAll(products: Type): Type {
	return products.map((e) => ({ ...e, selected: false }));
}
